import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import ClientFormFields from "./ClientFormFields";
import {
  CREATE_NEW_CLIENT,
  UPDATE_CLIENT_INFO,
} from "../../../../utils/GraphQL/mutations";
import { GET_ALL_REFERRED_BY_OPTIONS } from "../../../../utils/GraphQL/queries";
import CreateClientValidator from "../../../../utils/formValidators/createClientValidate";
import phoneNumberFormat from "../../../../utils/formatters/phoneNumberFormat";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";
import pasteKeapNote from "../../../../utils/formatters/pasteKeapNote";
import pasteKeapAddress from "../../../../utils/formatters/pasteKeapAddress";
import loadPlaces from "../../../../utils/loadPlaces";
import Auth from "../../../../utils/auth";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import "./clientForm.css";

/**
 * ClientFormModal — create (and, from P3, edit) a client in one BdrModal.
 *
 * Replaces the inline `CreateClient` surfaces on the Clients page and the
 * Messages page. Plan: `plans/plan_client_form_modal_2026_10_02.md`.
 *
 * This component owns everything stateful; `ClientFormFields` is presentational
 * and receives results. The division is in the plan (§5 P1) and is what keeps
 * the field set usable from both modes.
 *
 * ─────────────────────────────────────────────────────────────────
 * CONSTRAINTS THIS FILE IMPLEMENTS. Each was a review finding; each has a
 * step in the plan's §6 that fails if it is broken.
 *
 * §4.3  SNAPSHOT THE ADOPTED PHONE ON OPEN. The Messages menu item fires
 *       `handleNewClientDisplay(true)` and then `handleClose(true)`, and the
 *       close clears the menu's row. Reading `passedPhnObj` as a live prop
 *       would therefore see it go null a tick after opening, and the client
 *       would be created with no phone and an unlinked thread — silently. The
 *       prop is copied into state on the open transition and never read again.
 *
 * §4.4  SEED ON THE OPEN TRANSITION ONLY, FROM A DEEP COPY. Nothing re-seeds
 *       while the modal is open: on the client page an inbound SMS replaces
 *       `clientObj` and a `useEffect([client])` would wipe the user's edits
 *       mid-type. The copy is deep because phone ROWS would otherwise be
 *       shared with the caller's object.
 *
 * §4.6  BUILD VARIABLES FROM A WHITELIST, NEVER `...form`. The form carries
 *       `noteText` (a string) while the mutation wants a `ClientNoteInput`,
 *       and in edit mode the seed carries a populated `clientNotes` ARRAY.
 *       Spreading the form would send the wrong shape for both.
 *
 * §4.9  `closeOnBackdrop={false}` and a dirty check on close. A stray click
 *       discarding a 20-field form is not a trade worth offering. The confirm
 *       is an INLINE swap of the actions row, not a nested BdrModal — each
 *       open modal binds its own document keydown listener, so a stacked
 *       confirm would run both `onClose`s on one Escape.
 *
 * §4.10 Snackbar via `useSnackbar()`, never an optional prop. An optional
 *       `handleSnack` is exactly what made the old Messages create path throw
 *       after a successful write, leaving a blank form over a created client.
 *
 * §4.11 The note is stamped at SAVE time, not at the first keystroke.
 * ─────────────────────────────────────────────────────────────────
 */

/** The shape ClientFormFields edits. `noteText` is a string by design (§4.11). */
const BLANK_FORM = {
  firstName: "",
  lastName: "",
  spouseName: "",
  primaryEmail: "",
  secondaryEmail: "",
  phoneNumberIds: [{ phoneNumber: "", numberType: "", title: "" }],
  streetAddress: "",
  city: "",
  state: "Texas",
  zipCode: "",
  gateCode: "",
  clientStatus: "Lead",
  noteText: "",
};

/* Deep enough that no ROW object is shared with the caller (§4.4). */
const blankForm = () => ({
  ...BLANK_FORM,
  phoneNumberIds: BLANK_FORM.phoneNumberIds.map((row) => ({ ...row })),
});

/**
 * Seed the EDIT form from a client.
 *
 * §4.1 — `lastName` and `primaryEmail` are coerced to `""`, and that line is
 * load-bearing for **586 of 4819 prod clients (12.2%)** who have neither.
 * TWO gates depend on it, which is why it cannot be quietly dropped:
 *   - `UPDATE_CLIENT_INFO` types both `String!`. An empty string satisfies
 *     that; `null` fails GraphQL variable validation outright.
 *   - `CreateClientValidator` accepts `primaryEmail === ""` but runs `null`
 *     through a regex, which fails — so the FIRST symptom of losing this is a
 *     "Please Enter Valid Primary Email" snack, not a GraphQL error, and a
 *     debugger looks in the wrong place entirely.
 *
 * §4.4 — every phone row is a fresh object. Sharing them with `clientObj` is
 * how typing a digit would rewrite the record the form was seeded from.
 */
/*
 * Show a stored number hyphenated, but ONLY when that is lossless. The
 * formatter keeps ten digits (after dropping a leading country-code 1); a
 * longer or odd stored value would come back truncated and then be SAVED
 * truncated on the next edit, so such a value is shown exactly as stored.
 */
const seedPhone = (raw) => {
  const stored = raw || "";
  const formatted = phoneNumberFormat(stored);
  const digits = stored.replace(/[^0-9]/g, "");
  const expected =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return formatted.replace(/[^0-9]/g, "") === expected ? formatted : stored;
};

const seedFromClient = (client) => ({
  firstName: client?.firstName || "",
  lastName: client?.lastName || "",
  spouseName: client?.spouseName || "",
  primaryEmail: client?.primaryEmail || "",
  secondaryEmail: client?.secondaryEmail || "",
  phoneNumberIds: (client?.phoneNumberIds || []).map((row) => ({
    _id: row?._id,
    phoneNumber: seedPhone(row?.phoneNumber),
    numberType: row?.numberType || "",
    title: row?.title || "",
  })),
  streetAddress: client?.streetAddress || "",
  city: client?.city || "",
  state: client?.state || "",
  zipCode: client?.zipCode || "",
  gateCode: client?.gateCode || "",
  clientStatus: client?.clientStatus || "Lead",
  /* Always blank: the note field ADDS a note, it never edits the existing
     ones. Seeding it with history would re-save old notes as new. */
  noteText: "",
});

const ClientFormModal = ({
  open,
  onClose,
  /** "create" | "edit" — edit arrives in P3 */
  mode = "create",
  /**
   * Create-from-Messages: the phone to adopt. Snapshotted on open (§4.3); the
   * caller may clear it immediately afterwards and that must not matter.
   */
  passedPhnObj,
  /** (client) => void — the caller decides what success means */
  onCreated,
  /** edit mode: the client to seed from. Read ONLY on the open transition. */
  client,
  /** edit mode: fired after a successful save */
  onUpdated,
  /**
   * edit mode: delete this client. Optional — the modal renders no Delete
   * without it.
   *
   * It lives here because the console deliberately removed Delete from its
   * rail ("not a surface-level action ... it moves to the edit/create-client
   * modal when that is built"), and the inline Edit branch that carried it in
   * the meantime is gone. Without this, deleting a client is unreachable.
   * BdrButton's `danger` variant supplies the confirm.
   */
  onDelete,
}) => {
  const isEdit = mode === "edit";
  const { handleSnack } = useSnackbar();
  const [form, setForm] = useState(blankForm);
  const [errorsArr, setErrorsArr] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [userName, setUserName] = useState("");

  /* §4.3: the adopted number, captured at open. */
  const [adoptedPhn, setAdoptedPhn] = useState(null);

  /* Referral is not a plain field — it is a lookup plus a free-text escape. */
  const { data: referredByData } = useQuery(GET_ALL_REFERRED_BY_OPTIONS);
  const referredByOptions = referredByData?.getAllReferredByOptions || [];
  const [referredBy, setReferredBy] = useState(null);
  const [referredByOther, setReferredByOther] = useState("");
  const [referredByIsOther, setReferredByIsOther] = useState(false);

  const [createNewClientGQL] = useMutation(CREATE_NEW_CLIENT);
  const [updateClientGQL] = useMutation(UPDATE_CLIENT_INFO);

  /* Address autocomplete */
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const addressWrapperRef = useRef(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressHighlight, setAddressHighlight] = useState(-1);

  /* What the form looked like when it opened, for the dirty check (§4.9). */
  const seedRef = useRef(null);
  const seedReferralRef = useRef("");
  const wasOpenRef = useRef(false);

  /*
   * §4.4 — seed ONLY on the false->true transition. Not keyed on the inputs,
   * deliberately: a dependency on `passedPhnObj` would re-seed when the caller
   * clears it, which is the §4.3 failure in a different costume.
   */
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const next = isEdit ? seedFromClient(client) : blankForm();
      setForm(next);
      seedRef.current = JSON.stringify(next);
      setAdoptedPhn(passedPhnObj?._id ? { ...passedPhnObj } : null);
      setErrorsArr([]);
      setConfirmDiscard(false);

      /* Referral has three states on the wire: a catalog option, free text
         ("Other"), or nothing. Seeded the same way the edit branch did. */
      const rb = isEdit ? client?.referredBy : null;
      if (rb?.sourceOptionId) {
        setReferredBy({ source: rb.source, sourceOptionId: rb.sourceOptionId });
        setReferredByIsOther(false);
        setReferredByOther("");
      } else if (rb?.source) {
        setReferredBy(null);
        setReferredByIsOther(true);
        setReferredByOther(rb.source);
      } else {
        setReferredBy(null);
        setReferredByIsOther(false);
        setReferredByOther("");
      }
      /* Baseline for the dirty check, built from the SAME values just set —
         not from state, which has not re-rendered yet. */
      seedReferralRef.current = JSON.stringify([
        rb?.sourceOptionId || null,
        !rb?.sourceOptionId && !!rb?.source,
        (rb?.sourceOptionId ? "" : rb?.source || "").trim(),
      ]);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setAddressHighlight(-1);
    }
    wasOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /*
   * SYNCHRONOUS. `Auth.getProfile()` decodes the JWT and returns the payload
   * (or `false`) — it is not a promise, so `.then()` on it throws
   * "getProfile(...).then is not a function" at mount. The code this replaced
   * used `await`, which happens to tolerate a non-thenable; that is what hid
   * the signature.
   */
  useEffect(() => {
    const user = Auth.getProfile();
    if (user?.data?.firstName) setUserName(user.data.firstName);
  }, []);

  /*
   * §4.7 — Places through the shared promise, behind an `alive` guard so a
   * resolve after close writes into nothing. The old code set these refs from
   * a script `onload` closure, which could outlive the instance that made it.
   */
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    loadPlaces()
      .then((places) => {
        if (!alive) return;
        autocompleteService.current = new places.AutocompleteService();
        placesService.current = new places.PlacesService(
          document.createElement("div")
        );
      })
      .catch(() => {
        /* Autocomplete is a convenience; the address fields still type. */
      });
    return () => {
      alive = false;
    };
  }, [open]);

  /* Close the suggestion list on an outside click. */
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event) => {
      if (
        addressWrapperRef.current &&
        !addressWrapperRef.current.contains(event.target)
      ) {
        setShowAddressSuggestions(false);
        setAddressHighlight(-1);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const fetchAddressSuggestions = useCallback((input) => {
    if (!autocompleteService.current || !input || input.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    autocompleteService.current.getPlacePredictions(
      { input, types: ["address"], componentRestrictions: { country: "us" } },
      (predictions, status) => {
        const ok = window.google?.maps?.places?.PlacesServiceStatus?.OK;
        setAddressSuggestions(status === ok && predictions ? predictions : []);
      }
    );
  }, []);

  const handleAddressSelect = useCallback((placeId) => {
    if (!placesService.current) return;
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setAddressHighlight(-1);

    placesService.current.getDetails(
      { placeId, fields: ["address_components"] },
      (place, status) => {
        const ok = window.google?.maps?.places?.PlacesServiceStatus?.OK;
        if (status !== ok || !place) return;

        let streetNumber = "";
        let route = "";
        let city = "";
        let state = "";
        let zipCode = "";

        for (const comp of place.address_components) {
          const types = comp.types;
          if (types.includes("street_number")) streetNumber = comp.long_name;
          else if (types.includes("route")) route = comp.long_name;
          else if (types.includes("locality")) city = comp.long_name;
          else if (types.includes("sublocality_level_1") && !city)
            city = comp.long_name;
          else if (types.includes("administrative_area_level_1"))
            state = comp.long_name;
          else if (types.includes("postal_code")) zipCode = comp.long_name;
        }

        const streetAddress = streetNumber ? `${streetNumber} ${route}` : route;
        setForm((prev) => ({
          ...prev,
          streetAddress: capitalizeFirstLetters(streetAddress),
          city: capitalizeFirstLetters(city),
          state: capitalizeFirstLetters(state),
          zipCode,
        }));
      }
    );
  }, []);

  const handleAddressKeyDown = (event) => {
    if (!showAddressSuggestions || addressSuggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setAddressHighlight((prev) =>
        prev < addressSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setAddressHighlight((prev) =>
        prev > 0 ? prev - 1 : addressSuggestions.length - 1
      );
    } else if (event.key === "Enter" && addressHighlight >= 0) {
      event.preventDefault();
      handleAddressSelect(addressSuggestions[addressHighlight].place_id);
    } else if (event.key === "Escape") {
      /*
       * §4.8 — STOP THIS ESCAPE HERE. BdrModal listens for Escape on
       * `document`, and this portal's container IS document.body, so without
       * stopPropagation both run: the list closes AND the modal tries to, which
       * with the dirty check means a "discard changes?" prompt for the sin of
       * dismissing an address dropdown.
       */
      event.stopPropagation();
      setShowAddressSuggestions(false);
      setAddressHighlight(-1);
    }
  };

  /* ── paste helpers (modal-owned; the fields component just calls them) ── */

  const handlePastePhone = async () => {
    const text = await navigator?.clipboard?.readText();
    const phnNum = phoneNumberFormat(String(text || "").replace(/[^0-9]/gi, ""));
    /* Maps a copy — the original wrote `arr[0].phoneNumber = ...` straight
       through a shallow copy, which is the §4.4 mutation in miniature. */
    setForm((prev) => ({
      ...prev,
      phoneNumberIds: prev.phoneNumberIds.map((row, i) =>
        i === 0 ? { ...row, phoneNumber: phnNum } : row
      ),
    }));
  };

  const handlePasteAddress = async () => {
    const addressObj = await pasteKeapAddress(navigator?.clipboard, handleSnack);
    if (!addressObj) return;
    setForm((prev) => ({ ...prev, ...addressObj }));
  };

  const handlePasteKeap = async () => {
    const text = await pasteKeapNote(navigator?.clipboard, handleSnack);
    if (!text) return;
    setForm((prev) => ({
      ...prev,
      noteText: `${(prev.noteText || "").trim()}\n${text}`.trim(),
    }));
  };

  /* ── dirty check + close (§4.9) ── */

  /*
   * Dirty = the fields differ from the seed, OR the referral differs from what
   * the client arrived with. The referral cannot be compared against "empty"
   * the way the fields can — an edit opens with one already selected, so
   * treating "a referral exists" as dirty would prompt on every close.
   */
  const referralKey = () =>
    JSON.stringify([
      referredBy?.sourceOptionId || null,
      referredByIsOther,
      referredByOther.trim(),
    ]);

  const isDirty = () => {
    if (!seedRef.current) return false;
    return (
      JSON.stringify(form) !== seedRef.current ||
      referralKey() !== seedReferralRef.current
    );
  };

  const requestClose = () => {
    if (saving) return;
    if (isDirty()) {
      setConfirmDiscard(true);
      return;
    }
    onClose?.();
  };

  const discardAndClose = () => {
    setConfirmDiscard(false);
    onClose?.();
  };

  /* ── save ── */

  const handleSave = async () => {
    setErrorsArr([]);

    /*
     * The validator needs `existingPhn` or it demands a typed phone row, which
     * would make the adopt path unreachable (§4.5). It also reads the form's
     * own fields, so it gets the form shape, not the mutation shape.
     */
    const validator = new CreateClientValidator({
      ...form,
      clientNotes: { note: form.noteText },
      existingPhn: adoptedPhn?._id,
    });

    const valid = await validator.createClientIsValid();
    if (valid !== true) {
      setErrorsArr(Array.isArray(valid) ? valid : [String(valid)]);
      return;
    }

    const referredByVar = referredByIsOther
      ? referredByOther.trim()
        ? { source: referredByOther.trim(), sourceOptionId: null }
        : null
      : referredBy;

    /* §4.11 — the note is stamped HERE, at save, in both modes. */
    const noteVar = form.noteText.trim()
      ? {
          note: form.noteText.trim(),
          dateCreated: Date.now().toString(),
          dateUpdated: "",
          updatedBy: "",
          createdBy: userName,
        }
      : null;

    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await updateClientGQL({
          variables: {
            clientId: client?._id,
            firstName: form.firstName,
            /* §4.1 — these two are `String!`. The seed coerced them to ""; do
               not reintroduce a null here. */
            lastName: form.lastName || "",
            primaryEmail: form.primaryEmail || "",
            spouseName: form.spouseName,
            secondaryEmail: form.secondaryEmail,
            streetAddress: form.streetAddress,
            city: form.city,
            state: form.state,
            zipCode: form.zipCode,
            gateCode: form.gateCode,
            clientStatus: form.clientStatus,
            /*
             * §4.5 — `_id` is what relinks a row; a row that arrives without
             * one is treated as dropped and `cleanupDroppedPhones` DELETES the
             * phone document.
             *
             * `title` is deliberately NOT sent (phone-uniqueness, 2026-09-04):
             * it is display-only and the server stamps its own default only
             * when this client solely owns the doc, so a shared number's title
             * is not clobbered by whoever saved last. Create DOES send it —
             * there is no existing doc to protect.
             */
            phoneNumberIds: form.phoneNumberIds.map((row) => ({
              _id: row._id,
              phoneNumber: row.phoneNumber,
              numberType: row.numberType,
            })),
            clientNotes: noteVar,
            referredBy: referredByVar,
          },
        });

        if (!data?.updateClient?._id) {
          handleSnack("Something went wrong. Please try again.", 2500);
          return;
        }

        seedRef.current = JSON.stringify(form);
        seedReferralRef.current = referralKey();
        handleSnack("Client Updated", 2500);
        onUpdated?.(data.updateClient);
        return;
      }

      /*
       * §4.6 — EXPLICIT WHITELIST. Never `...form`: it carries `noteText`,
       * which is not a mutation variable, and in edit mode the seed carries a
       * populated `clientNotes` array where the variable wants one input
       * object.
       *
       * §4.11 — the note is stamped HERE, at save.
       */
      const { data } = await createNewClientGQL({
        variables: {
          firstName: form.firstName,
          lastName: form.lastName,
          spouseName: form.spouseName,
          primaryEmail: form.primaryEmail,
          secondaryEmail: form.secondaryEmail,
          streetAddress: form.streetAddress,
          city: form.city,
          state: form.state,
          zipCode: form.zipCode,
          gateCode: form.gateCode,
          clientStatus: form.clientStatus,
          phoneNumberIds: form.phoneNumberIds.map((row) => ({
            phoneNumber: row.phoneNumber,
            numberType: row.numberType,
            title: row.title || "",
          })),
          clientNotes: noteVar,
          existingPhnId: adoptedPhn?._id || null,
          referredBy: referredByVar,
        },
      });

      /* `created`, not `client` — `client` is the EDIT prop, and shadowing it
         inside this function puts the edit branch above in a temporal dead
         zone for its own prop. */
      const created = data?.createNewClient;
      if (!created?._id) {
        handleSnack("Something went wrong. Please try again.", 2500);
        return;
      }

      /* Clear the dirty baseline BEFORE handing control back, so a caller that
         closes us cannot trip the discard prompt on a successful save. */
      seedRef.current = JSON.stringify(form);
      seedReferralRef.current = referralKey();
      onCreated?.(created);
    } catch (error) {
      handleSnack(error?.message || "Something went wrong.", 4000);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <BdrModal
      open
      onClose={requestClose}
      variant="panel"
      size="lg"
      title={mode === "edit" ? "Edit Client" : "New Client"}
      /* §4.9 — a stray backdrop click must not discard a long form. */
      closeOnBackdrop={false}
      actions={
        confirmDiscard ? (
          <>
            <span className="cform-discard-msg">Discard changes?</span>
            <BdrButton onClick={() => setConfirmDiscard(false)}>
              Keep editing
            </BdrButton>
            <BdrButton variant="danger" confirm={false} onClick={discardAndClose}>
              Discard
            </BdrButton>
          </>
        ) : (
          <>
            {isEdit && onDelete && (
              <BdrButton
                variant="danger"
                disabled={saving}
                note="This permanently removes the client and cannot be undone."
                confirmTitle="Delete client?"
                confirmLabel="Delete"
                onClick={onDelete}
              >
                Delete
              </BdrButton>
            )}
            <BdrButton onClick={requestClose} disabled={saving}>
              Cancel
            </BdrButton>
            <BdrButton
              variant="confirm-solid"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Client"}
            </BdrButton>
          </>
        )
      }
    >
      {errorsArr.length > 0 && (
        <ul className="cform-errors">
          {errorsArr.map((err, i) => (
            <li
              key={`cform-err-${i}`}
              className="bdr-alert bdr-alert--danger bdr-alert--start"
              role="alert"
            >
              {err}
            </li>
          ))}
        </ul>
      )}

      <ClientFormFields
        mode={mode}
        value={form}
        onChange={setForm}
        passedPhnObj={adoptedPhn}
        disabled={saving}
        referral={{
          options: referredByOptions,
          value: referredBy,
          isOther: referredByIsOther,
          otherText: referredByOther,
          onSelect: (val) => {
            if (val === "") {
              setReferredBy(null);
              setReferredByIsOther(false);
              setReferredByOther("");
              return;
            }
            if (val === "__other__") {
              setReferredByIsOther(true);
              setReferredBy(null);
              return;
            }
            const selected = referredByOptions.find((opt) => opt._id === val);
            if (selected) {
              setReferredBy({
                source: selected.source,
                sourceOptionId: selected._id,
              });
            }
            setReferredByIsOther(false);
            setReferredByOther("");
          },
          onOtherText: setReferredByOther,
        }}
        address={{
          suggestions: addressSuggestions,
          show: showAddressSuggestions,
          highlight: addressHighlight,
          wrapperRef: addressWrapperRef,
          onInput: (next) => {
            fetchAddressSuggestions(next);
            setShowAddressSuggestions(true);
          },
          onFocus: (current) => {
            if ((current || "").length >= 3) {
              fetchAddressSuggestions(current);
              setShowAddressSuggestions(true);
            }
          },
          onBlur: () => {
            /* Delayed so a mousedown on a suggestion lands first. */
            setTimeout(() => {
              setShowAddressSuggestions(false);
              setAddressHighlight(-1);
            }, 200);
          },
          onKeyDown: handleAddressKeyDown,
          onSelect: handleAddressSelect,
        }}
        onPastePhone={handlePastePhone}
        onPasteAddress={handlePasteAddress}
        onPasteKeap={handlePasteKeap}
      />
    </BdrModal>
  );
};

export default ClientFormModal;
